import React, { useState, useEffect, useCallback } from 'react';
import {
    X, Shield, AlertTriangle, CheckCircle2,
    BarChart3, RefreshCw, UserCheck, AlertCircle, Download,
    Maximize2, Minimize2
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    BarChart,
    DataTable,
    ProgressRing,
    ExpandableVisualization
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';
import ActivityReminderSection from './ActivityReminderSection';

const ComplianceAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 400, maxWidth: 800, defaultWidth: 600,
        storageKey: 'complianceAnalyticsDrawerWidth',
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/analytics/compliance', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(response.data.analytics);
        } catch (error) {
            console.error('Error fetching compliance analytics:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'compliance', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'compliance-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Compliance analytics exported');
        } catch { toast.error('Export failed'); }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
            document.body.style.overflow = 'hidden';
        } else {
            setData(null);
            document.body.style.overflow = 'unset';
            setLoading(true);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{
                    width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                    maxWidth: '100vw',
                    transition: isDragging ? 'none' : 'width 0.3s ease',
                }}
            >
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}
                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Shield className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Compliance Analytics</h2>
                            <p className="text-sm text-gray-500">Violations and aduit readiness monitoring</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide min-w-0">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                            <p className="text-gray-500 font-medium">Loading analytics...</p>
                        </div>
                    ) : data ? (
                        <div className="space-y-6">
                            {/* Audit Readiness Score */}
                            {data.auditReadiness && (
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="font-semibold text-gray-800">Audit Readiness Score</h3>
                                            <p className="text-sm text-gray-500">Overall compliance completion</p>
                                        </div>
                                        <ProgressRing
                                            value={data.auditReadiness.score}
                                            size={100}
                                            label="Score"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-gray-900">{data.auditReadiness.totalTasks}</p>
                                            <p className="text-sm text-gray-500">Total Tasks</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-green-600">{data.auditReadiness.completedAssignments}</p>
                                            <p className="text-sm text-gray-500">Completed</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-amber-600">{data.auditReadiness.totalAssignments - data.auditReadiness.completedAssignments}</p>
                                            <p className="text-sm text-gray-500">Pending</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Compliance by Department */}
                            {data.byDepartment && (
                                <ExpandableVisualization
                                    title="Compliance by Department"
                                    data={data.byDepartment}
                                    columns={[{ key: 'department', label: 'Department' }, { key: 'completionRate', label: 'Completion Rate %' }, { key: 'completed', label: 'Completed' }, { key: 'overdue', label: 'Overdue' }]}
                                    exportFilename="compliance-by-department"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Compliance by Department</h3>
                                        <BarChart
                                            data={data.byDepartment.map(d => ({
                                                label: d.department,
                                                value: d.completionRate,
                                                suffix: '%',
                                                color: d.completionRate >= 80 ? '#2563EB' : d.completionRate >= 50 ? '#60A5FA' : '#EF4444'
                                            }))}
                                            horizontal
                                            showValues
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Overdue Compliance Tasks */}
                            {data.overdueTasks && data.overdueTasks.length > 0 && (
                                <ExpandableVisualization
                                    title="Overdue Compliance Tasks"
                                    subtitle="Immediate attention required"
                                    data={data.overdueTasks}
                                    columns={[{ key: 'taskTitle', label: 'Task' }, { key: 'employeeName', label: 'Employee' }, { key: 'department', label: 'Department' }, { key: 'daysOverdue', label: 'Days Overdue' }]}
                                    exportFilename="overdue-compliance-tasks"
                                >
                                    <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden">
                                        <div className="p-4 border-b border-red-200">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="w-5 h-5 text-red-600" />
                                                <h3 className="font-semibold text-red-800">Overdue Compliance Tasks</h3>
                                            </div>
                                            <p className="text-sm text-red-700 mt-1">Immediate attention required</p>
                                        </div>
                                        <DataTable
                                            data={data.overdueTasks}
                                            columns={[
                                                { key: 'taskTitle', label: 'Task', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                { key: 'employeeName', label: 'Employee' },
                                                { key: 'department', label: 'Department' },
                                                {
                                                    key: 'daysOverdue', label: 'Days Overdue', render: (val) => (
                                                        <span className="text-red-600 font-semibold">{val} days</span>
                                                    )
                                                }
                                            ]}
                                            searchable
                                            searchKeys={['taskTitle', 'employeeName', 'department']}
                                            pageSize={5}
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Compliance Gaps */}
                            {data.complianceGaps && data.complianceGaps.length > 0 && (
                                <ExpandableVisualization
                                    title="Compliance Gaps"
                                    subtitle="Employees missing compliance requirements"
                                    data={data.complianceGaps}
                                    columns={[{ key: 'name', label: 'Employee' }, { key: 'department', label: 'Department' }, { key: 'completed', label: 'Completed' }, { key: 'totalRequired', label: 'Required' }, { key: 'gapPercentage', label: 'Gap %' }]}
                                    exportFilename="compliance-gaps"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                        <div className="p-4 border-b border-gray-200">
                                            <h3 className="font-semibold text-gray-800">Compliance Gaps</h3>
                                            <p className="text-sm text-gray-500">Employees missing compliance requirements</p>
                                        </div>
                                        <DataTable
                                            data={data.complianceGaps}
                                            columns={[
                                                { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                { key: 'department', label: 'Department' },
                                                { key: 'completed', label: 'Completed', render: (val, row) => `${val}/${row.totalRequired}` },
                                                {
                                                    key: 'gapPercentage', label: 'Gap', render: (val) => (
                                                        <span className={`font-medium ${val > 50 ? 'text-red-600' : val > 25 ? 'text-amber-600' : 'text-green-600'}`}>
                                                            {val}%
                                                        </span>
                                                    )
                                                }
                                            ]}
                                            searchable
                                            searchKeys={['name', 'department']}
                                            pageSize={5}
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <StatCard
                                    title="Compliance Rate"
                                    value={`${data.complianceRate || 0}%`}
                                    icon={CheckCircle2}
                                    color={data.complianceRate >= 90 ? 'green' : data.complianceRate >= 70 ? 'amber' : 'red'}
                                    trend={data.complianceTrend}
                                />
                                <StatCard
                                    title="Active Violations"
                                    value={data.activeViolations || 0}
                                    icon={AlertTriangle}
                                    color="red"
                                />
                                <StatCard
                                    title="Open Tasks"
                                    value={data.openComplianceTasks || 0}
                                    icon={UserCheck}
                                    color="blue"
                                />
                                <StatCard
                                    title="Employees Affected"
                                    value={data.employeesWithViolations || 0}
                                    icon={BarChart3}
                                    color="gray"
                                />
                            </div>

                            {/* Violations Chart */}
                            {data.violationsHistory && (
                                <ExpandableVisualization
                                    title="Compliance Trend (Last 30 Days)"
                                    data={data.violationsHistory}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'count', label: 'Violations' }]}
                                    exportFilename="compliance-trend"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] p-6 shadow-sm">
                                        <h3 className="font-semibold text-gray-800 mb-4">Compliance Trend (Last 30 Days)</h3>
                                        <BarChart
                                            data={data.violationsHistory.map(d => ({
                                                label: d.date,
                                                value: d.count,
                                                color: '#EF4444'
                                            }))}
                                            showValues
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Top Violations Table */}
                            {data.topViolations && data.topViolations.length > 0 && (
                                <ExpandableVisualization
                                    title="Most Common Violations"
                                    data={data.topViolations}
                                    columns={[{ key: 'title', label: 'Violation' }, { key: 'count', label: 'Occurrences' }, { key: 'department', label: 'Dept' }, { key: 'avgResolutionTime', label: 'Avg Resolution (Days)' }]}
                                    exportFilename="top-violations"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                        <div className="p-4 border-b border-[#E5E1DD]">
                                            <h3 className="font-semibold text-gray-800">Most Common Violations</h3>
                                        </div>
                                        <DataTable
                                            data={data.topViolations}
                                            columns={[
                                                { key: 'title', label: 'Violation Type', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                { key: 'count', label: 'Occurrences', render: (val) => <span className="text-red-600 font-medium">{val}</span> },
                                                { key: 'department', label: 'Most Affected Dept' },
                                                {
                                                    key: 'avgResolutionTime',
                                                    label: 'Avg Resolution',
                                                    render: (val) => val ? `${val} days` : '-'
                                                }
                                            ]}
                                            searchable
                                            searchKeys={['title']}
                                            pageSize={5}
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* AI Chat Panel */}
                            <AnalyticsAIPanel
                                analyticsContext={{
                                    type: 'compliance',
                                    title: 'Compliance Analytics',
                                    data: data,
                                }}
                            />

                            {/* Activity Reminder Section */}
                            <ActivityReminderSection
                                activityType="compliance"
                                activityId={data?.overdueTasks?.[0]?.taskId}
                                activityTitle="Compliance Tasks"
                            />

                            {/* Notification Settings Panel */}
                            <NotificationSettingsPanel
                                analyticsContext={{
                                    type: 'compliance',
                                    title: 'Compliance Analytics',
                                    data: data,
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <div className="bg-gray-50 rounded-lg p-6 max-w-sm mx-auto border border-gray-200">
                                <BarChart3 className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-gray-900 font-medium mb-1">No Analytics Available</h3>
                                <p className="text-sm text-gray-500">
                                    We couldn't load compliance analytics at this time.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default ComplianceAnalyticsDrawer;
